import { Helmet } from "react-helmet-async";
import { useParams, Navigate, Link } from "react-router-dom";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import Breadcrumbs from "@/components/blog/Breadcrumbs";
import AuthorByline from "@/components/blog/AuthorByline";
import MarkdownRenderer from "@/components/blog/MarkdownRenderer";
import RelatedPosts from "@/components/blog/RelatedPosts";
import NextRead from "@/components/blog/NextRead";
import AppDownloadCTA from "@/components/blog/AppDownloadCTA";
import StickyMobileAppBar from "@/components/blog/StickyMobileAppBar";
import { getPostBySlug, CATEGORIES, POST_REDIRECTS } from "@/content/posts";
import { SITE_URL } from "@/constants/storeLinks";
import { Clock } from "lucide-react";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  if (slug && POST_REDIRECTS[slug]) {
    return <Navigate to={`/blog/${POST_REDIRECTS[slug]}`} replace />;
  }
  const post = slug ? getPostBySlug(slug) : undefined;
  if (!post) return <Navigate to="/blog" replace />;

  const category = CATEGORIES[post.category];
  const canonical = `${SITE_URL}/blog/${post.slug}`;
  const ogImage = `${SITE_URL}${post.cover}`;
  const metaTitle = post.seoTitle ?? `${post.title} | PieFolio AI`;
  const metaDesc = post.seoDescription ?? post.description;
  const isBrokerPost = post.category === "brokers";

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.description,
    image: [ogImage],
    datePublished: post.date,
    dateModified: post.lastUpdated,
    author: {
      "@type": "Organization",
      name: post.authorObj.name,
      url: `${SITE_URL}/blog`,
    },
    publisher: {
      "@type": "Organization",
      name: "PieFolio AI",
      logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.png` },
    },
    mainEntityOfPage: canonical,
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
      {
        "@type": "ListItem",
        position: 3,
        name: category.name,
        item: `${SITE_URL}/blog/category/${category.slug}`,
      },
      { "@type": "ListItem", position: 4, name: post.title, item: canonical },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:image" content={ogImage} />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:modified_time" content={post.lastUpdated} />
        <meta property="article:author" content={post.authorObj.name} />
        {post.tags.map((t) => (
          <meta key={t} property="article:tag" content={t} />
        ))}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={metaDesc} />
        <meta name="twitter:image" content={ogImage} />
        <script type="application/ld+json">{JSON.stringify(articleLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      </Helmet>

      <Header />

      <main className="pt-24 md:pt-32 pb-20 px-6 md:px-8">
        <div className="container mx-auto max-w-6xl">
          <Breadcrumbs
            items={[
              { label: "Home", to: "/" },
              { label: "Blog", to: "/blog" },
              { label: category.name, to: `/blog/category/${category.slug}` },
              { label: post.title },
            ]}
          />

          {/* Hero */}
          <header className="mt-6 mb-8 max-w-3xl">
            <Link
              to={`/blog/category/${category.slug}`}
              className="inline-block text-xs uppercase tracking-widest text-primary font-semibold mb-3 hover:opacity-80"
            >
              {category.name}
            </Link>
            <h1 className="text-3xl md:text-5xl font-bold text-foreground tracking-tight leading-tight">
              {post.title}
            </h1>
            <p className="mt-5 text-lg text-foreground/70 leading-relaxed">
              {post.description}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-6">
              <AuthorByline author={post.authorObj} lastUpdated={post.lastUpdated} />
              <div className="flex items-center gap-2 text-sm text-foreground/60">
                <Clock className="w-4 h-4" />
                <span>{post.readingTime} min read</span>
              </div>
            </div>
          </header>

          {/* Cover */}
          <div className="rounded-2xl overflow-hidden border border-border mb-10">
            <img
              src={post.cover}
              alt={post.title}
              width={1600}
              height={900}
              fetchPriority="high"
              decoding="async"
              className="w-full h-auto"
            />
          </div>

          {/* Hero CTA */}
          <AppDownloadCTA source={`post-${post.slug}-hero`} className="mb-10" />

          {/* Body */}
          <article className="min-w-0">
            <MarkdownRenderer
              content={post.content}
              source={`post-${post.slug}`}
              isBrokerPost={isBrokerPost}
            />
          </article>

          {/* End-of-post CTA */}
          <div className="mt-12">
            <AppDownloadCTA source={`post-${post.slug}-end`} />
          </div>

          <NextRead slug={post.slug} />

          <RelatedPosts slug={post.slug} />
        </div>
      </main>

      <Footer />
      <StickyMobileAppBar />
    </>
  );
};

export default BlogPost;
